import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { BreakpointDown } from '@travel-frontend/core';
import { range } from 'lodash';
import { Trans, useTranslation } from 'next-i18next';
import { forwardRef } from 'react';
import { Content } from '../../../../components/SearchForm/components/TravellersWidget/components/Content.js';
import { Headline } from '../../../../components/SearchForm/components/TravellersWidget/components/Headline.js';
import { HotlineWidget } from '../../../../components/SearchForm/components/TravellersWidget/components/HotlineWidget/index.js';
import { Person } from '../../../../components/SearchForm/components/TravellersWidget/components/Person.js';
import { PlaceholderPrefix } from '../../../../components/SearchForm/components/TravellersWidget/components/PlaceholderPrefix.js';
import { TravellerCount } from '../../../../components/SearchForm/components/TravellersWidget/components/TravellerCount.js';
import { UnderageTravellerList } from '../../../../components/SearchForm/components/TravellersWidget/components/UnderageTravellerList.js';
import { WidgetChrome } from '../../../../components/SearchForm/components/WidgetChrome/index.js';
import { WidgetOverlay } from '../../../../components/SearchForm/components/WidgetOverlay/index.js';
import { Alert, Button, LabelSmall, LabelSmallBold, Select, Stepper } from '../../../../primitives/index.js';
import { qaId } from '../../../../utils/index.js';
import { SEARCH_FORM_QA_ID } from '../../../../utils/qaId.js';
export const TravellersWidget = forwardRef(function TravellersWidget({
  type,
  open,
  data,
  hasMissingChildrenAgeMessage,
  childrenAgesValidationState,
  applyButtonDisabled,
  handlers,
  config,
  layout,
  dimming = false,
  widgetHead,
  ...restProps
}, ref) {
  const {
    t
  } = useTranslation();
  const {
    adultCount,
    childrenAges
  } = data || {};
  const {
    onApply,
    onClose,
    onReset,
    onAdultCountChange,
    onChildAgeChange,
    onChildrenCountChange
  } = handlers;
  const {
    adultCountMin,
    adultCountMax,
    childrenCountMax,
    childrenCountMin,
    childrenAgeMin,
    childrenAgeMax,
    multiRoomBooking,
    maxTravellers
  } = config;
  const childrenCount = childrenAges ? childrenAges.length : 0;
  const childrenAgeOptions = range(childrenAgeMin, childrenAgeMax).map(index => ({
    value: index,
    label: index === 1 ? t('travellersWidget:childrenAgeOptions.optionInfant', {
      age: 2
    }) : t('travellersWidget:childrenAgeOptions.optionChild', {
      age: index
    })
  }));
  const getActiveSelectOption = childIndex => {
    return Array.isArray(childrenAges) && childrenAges[childIndex] != null ? childrenAgeOptions.find(option => option.value === childrenAges[childIndex]) : null;
  };
  const multiRoomBookingHint = multiRoomBooking ? _jsx(HotlineWidget
  // @ts-expect-error TS(2322) FIXME: Type 'Partial<ServiceConfig>' is not assignable to... Remove this comment to see the full error message
  , {
    // @ts-expect-error TS(2322) FIXME: Type 'Partial<ServiceConfig>' is not assignable to... Remove this comment to see the full error message
    config: multiRoomBooking,
    embedded: type === 'popover'
  }) : undefined;
  const maxTravellersReached = Boolean(adultCount && adultCount + childrenCount >= maxTravellers);
  return _jsx(WidgetOverlay, {
    type: type,
    open: open,
    dim: dimming || layout === 'compact' || layout === 'sidebar',
    children: _jsx(WidgetChrome, {
      ...restProps,
      ref: ref,
      type: type,
      head: widgetHead,
      title: t('travellersWidget:title.dialog'),
      onClose: onClose,
      aside: multiRoomBookingHint,
      controls: [_jsx(Button, {
        onClick: onReset,
        category: "secondary",
        children: t('travellersWidget:button.reset')
      }, "reset-button"), _jsx(Button, {
        onClick: onApply,
        deactivated: applyButtonDisabled,
        children: t('travellersWidget:button.apply')
      }, "apply-button")],
      allowOverflow: type === 'popover',
      "data-testid": "travellers-widget",
      reverse: layout === 'compact' || layout === 'sidebar',
      children: _jsxs(Content, {
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellersLayer'),
        children: [_jsx(BreakpointDown, {
          matches: "md",
          children: _jsx(Headline, {
            children: t('travellersWidget:title.content')
          })
        }), _jsxs(TravellerCount, {
          children: [_jsx(Person, {
            children: t('travellersWidget:stepper.adultLabel')
          }), _jsx(Stepper, {
            "data-testid": "adult-count",
            min: adultCountMin,
            max: adultCountMax,
            disableIncrement: maxTravellersReached,
            value: adultCount ?? 0,
            onChange: onAdultCountChange
          }), _jsxs(Person, {
            children: [t('travellersWidget:stepper.childrenLabel'), _jsx(LabelSmall, {
              color: "contentSecondary",
              children: t('travellersWidget:stepper.childrenLabelHint', {
                min: childrenAgeMin - 1,
                max: childrenAgeMax - 1
              })
            })]
          }), _jsx(Stepper, {
            "data-testid": "children-count",
            min: childrenCountMin,
            max: childrenCountMax,
            disableIncrement: maxTravellersReached,
            value: childrenCount,
            onChange: onChildrenCountChange
          })]
        }), childrenCount > 0 && _jsxs(UnderageTravellerList, {
          "data-testid": "children-ages",
          children: [hasMissingChildrenAgeMessage && _jsx(Alert, {
            variant: "warning",
            caption: t('travellersWidget:alert.missingChildrenAge.caption', {
              count: childrenCount
            }),
            children: t('travellersWidget:alert.missingChildrenAge.content')
          }), _jsx(LabelSmallBold, {
            as: "span",
            children: t('travellersWidget:childrenAgeOptions.caption', {
              count: childrenCount
            })
          }), range(childrenCount).map(index => _jsx(Select, {
            // @ts-expect-error TS(2322) FIXME: Type '{ key: string; options: { value: number; lab... Remove this comment to see the full error message
            options: childrenAgeOptions,
            isSearchable: false,
            value: getActiveSelectOption(index),
            onChange: option => {
              onChildAgeChange(option.value, index);
            },
            menuPlacement: type === 'drawer' ? 'top' : undefined,
            placeholder: _jsx(Trans, {
              i18nKey: "travellersWidget:childrenAgeOptions.placeholder",
              values: {
                nr: index + 1
              },
              components: {
                bold: _jsx(PlaceholderPrefix, {})
              }
            }),
            formatSingleValue: state => ({
              value: t('travellersWidget:childrenAgeOptions.selectedValue', {
                nr: index + 1,
                optionLabel: state.data.label,
                interpolation: {
                  escapeValue: false
                }
              })
            }),
            highlighted: typeof childrenAgesValidationState[index] === 'boolean' ? !childrenAgesValidationState[index] : undefined
          }, `child${index}`))]
        }), type === 'drawer' && multiRoomBookingHint]
      })
    })
  });
});